// Voice care-plan medication normalisation.
//
// The voice assistant and the doctor console write DIFFERENT medication shapes
// into care_plan_data — carePlanMedications.js already documents the split:
//
//   doctor console  -> medication.{drug_name, duration, duration_unit, ...}
//   voice assistant -> medication.{name,      period,   periodUnit,    ...}
//
// That difference is not cosmetic. care_plans/ runs a CPOE dual-write
// (main/ipd/manage_care_plan.py -> create_medication_order), and the backend
// reads ONLY the canonical keys:
//
//   compute_duration_days(med.get("duration"), med.get("duration_unit"))
//     -> days = max(1, round(max(1, num(duration, 1)) * days_per))
//
// A missing `duration` is not an error there — it silently becomes 1. So a
// dictated "for ten days" was being scheduled as a single day, with no warning
// anywhere. Likewise generate_medication_schedule() REJECTS an unrecognised
// frequency outright (MedicationOrderError), and because the dual-write runs
// inside transaction.atomic() the CarePlanData row unwinds with it — the whole
// entry vanishes and only the response's `skipped[]` says so.
//
// This module maps what the AI returns onto those canonical keys BEFORE the
// doctor reviews it, so the review screen shows the values that will actually
// be scheduled, and flags anything it cannot map rather than guessing.
//
// Deliberately import-free: anything that pulls in api/* drags axios's ESM
// build into Jest and the suite stops running.

// ── Canonical vocabularies — MIRROR main/ipd/medication_order.py ──────────────
// SLOT_FREQUENCY_MAP / HOURLY_FREQUENCIES / LONG_FREQUENCIES /
// IMMEDIATE_FREQUENCIES / AS_NEEDED_FREQUENCIES. Keep the two in step: a code
// absent from the backend's maps is rejected at order time.
export const SLOT_FREQUENCIES = ["OD", "BD", "TDS", "QID", "HS"];
export const HOURLY_FREQUENCIES = ["Q4H", "Q6H", "Q8H", "Q12H"];
export const LONG_FREQUENCIES = ["Weekly", "Monthly"];
export const IMMEDIATE_FREQUENCIES = ["STAT"];
export const AS_NEEDED_FREQUENCIES = ["SOS", "PRN"];

export const CANONICAL_FREQUENCIES = [
  ...SLOT_FREQUENCIES,
  ...HOURLY_FREQUENCIES,
  ...LONG_FREQUENCIES,
  ...IMMEDIATE_FREQUENCIES,
  ...AS_NEEDED_FREQUENCIES,
];

// compute_duration_days()'s days_per table. An unrecognised unit falls back to
// 1 there (treated as days), which is exactly the silent wrong answer this
// module exists to prevent — so we only ever emit a unit from this list.
export const CANONICAL_DURATION_UNITS = [
  "Hour(s)",
  "Day(s)",
  "Week(s)",
  "Month(s)",
  "Year(s)",
];

const collapse = (value) =>
  String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Second lookup key with the separators removed, so punctuation *inside* a code
// ("Q.6.H", "Q 6 H") still resolves. These fields are machine-generated, and the
// AI is not consistent about how it writes an abbreviation. Registered as an
// extra key rather than replacing `collapse`, because the word-spaced form is
// what multi-word aliases ("once daily") match on. Checked for collisions: no
// two entries below share a squashed form.
const squash = (value) => collapse(value).replace(/ /g, "");

// Spoken / written forms the AI realistically returns, mapped to the backend's
// codes. Only unambiguous phrasings are listed: anything not here stays blank
// and the doctor picks it, because a mis-mapped frequency reschedules the whole
// course. English only — the structured payload comes back in English even when
// the dictation language is Hindi or mixed; add a form here the day one is seen
// in a real response rather than guessing at it now.
const FREQUENCY_ALIASES = {
  OD: ["od", "qd", "once daily", "once a day", "one time a day", "daily", "every day", "1 0 0"],
  BD: ["bd", "bid", "twice daily", "twice a day", "two times a day", "2 times a day", "1 0 1"],
  TDS: ["tds", "tid", "thrice daily", "thrice a day", "three times a day", "3 times a day", "1 1 1"],
  QID: ["qid", "qds", "four times a day", "four times daily", "4 times a day"],
  HS: ["hs", "at night", "nightly", "at bedtime", "bedtime", "once at night", "night only"],
  Q4H: ["q4h", "every 4 hours", "4 hourly", "four hourly", "every four hours"],
  Q6H: ["q6h", "every 6 hours", "6 hourly", "six hourly", "every six hours"],
  Q8H: ["q8h", "every 8 hours", "8 hourly", "eight hourly", "every eight hours"],
  Q12H: ["q12h", "every 12 hours", "12 hourly", "twelve hourly", "every twelve hours"],
  Weekly: ["weekly", "once a week", "every week", "once weekly"],
  Monthly: ["monthly", "once a month", "every month", "once monthly"],
  STAT: ["stat", "immediately", "at once", "right now", "immediate"],
  SOS: ["sos", "as needed", "when required", "if required", "as required"],
  PRN: ["prn", "pro re nata"],
};

const FREQUENCY_BY_ALIAS = (() => {
  const index = new Map();
  const register = (text, code) => {
    index.set(collapse(text), code);
    // First writer wins: a squashed form must never shadow an exact spaced one.
    const squashed = squash(text);
    if (!index.has(squashed)) index.set(squashed, code);
  };
  CANONICAL_FREQUENCIES.forEach((code) => register(code, code));
  Object.entries(FREQUENCY_ALIASES).forEach(([code, aliases]) => {
    aliases.forEach((alias) => register(alias, code));
  });
  return index;
})();

/**
 * A canonical frequency code, or "" when the input cannot be mapped with
 * certainty. "" is a real answer: the caller blocks the save and the doctor
 * picks, rather than the backend defaulting it to OD.
 */
export function normalizeFrequency(raw) {
  const key = collapse(raw);
  if (!key) return "";
  return FREQUENCY_BY_ALIAS.get(key) || FREQUENCY_BY_ALIAS.get(squash(raw)) || "";
}

const DURATION_UNIT_ALIASES = {
  "Hour(s)": ["hour", "hours", "hr", "hrs", "h"],
  "Day(s)": ["day", "days", "d"],
  "Week(s)": ["week", "weeks", "wk", "wks", "w"],
  "Month(s)": ["month", "months", "mo", "mon", "mths"],
  "Year(s)": ["year", "years", "yr", "yrs", "y"],
};

const DURATION_UNIT_BY_ALIAS = (() => {
  const index = new Map();
  CANONICAL_DURATION_UNITS.forEach((unit) => index.set(collapse(unit), unit));
  Object.entries(DURATION_UNIT_ALIASES).forEach(([unit, aliases]) => {
    aliases.forEach((alias) => index.set(collapse(alias), unit));
  });
  return index;
})();

/** A canonical duration unit, or "" when unmappable. */
export function normalizeDurationUnit(raw) {
  const key = collapse(raw);
  if (!key) return "";
  return DURATION_UNIT_BY_ALIAS.get(key) || "";
}

/**
 * Split a spoken duration into { duration, duration_unit }.
 *
 * The AI puts the number and the unit in separate fields most of the time
 * (period: 10, periodUnit: "days") but not always — "10 days" arriving whole in
 * `period` is common enough that reading only the explicit unit field would
 * drop the unit and hand the backend a bare number. The explicit unit still
 * wins when both are present.
 *
 * duration is null when no number is present: null is "not stated", and the
 * caller must not turn it into 1.
 */
export function parseDuration(rawValue, rawUnit) {
  const text = String(rawValue == null ? "" : rawValue).trim();
  const explicitUnit = normalizeDurationUnit(rawUnit);

  const numberMatch = text.match(/-?\d+(?:\.\d+)?/);
  const parsed = numberMatch ? Number(numberMatch[0]) : null;
  const duration = parsed !== null && Number.isFinite(parsed) ? parsed : null;

  // Unit embedded alongside the number ("10 days"): read it off the remaining
  // text only when no explicit unit field was supplied.
  let embeddedUnit = "";
  if (!explicitUnit) {
    embeddedUnit = numberMatch
      ? normalizeDurationUnit(text.replace(numberMatch[0], ""))
      : normalizeDurationUnit(text);
  }

  return {
    duration,
    duration_unit: explicitUnit || embeddedUnit || "",
  };
}

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value === 0) return 0;
    if (value != null && String(value).trim() !== "") return value;
  }
  return "";
};

/**
 * Add the canonical medication keys to one medication-shaped object.
 *
 * ADDITIVE on purpose: the review form is rendered from care_plan_structures,
 * and that template may well declare `period`/`periodUnit` fields. Deleting the
 * voice keys would blank those inputs, so the originals are left exactly where
 * they were and the canonical keys are written alongside — which is all
 * create_medication_order() reads.
 *
 * Returns a new object; the input is not mutated.
 */
export function normalizeVoiceMedicationFields(med) {
  if (!med || typeof med !== "object" || Array.isArray(med)) return med;

  const out = { ...med };

  // drug_name: medicine_details() reads `drug_name or name`, so voice's `name`
  // already works — but the console's own table renderers and
  // buildMedicinesFromEntries read drug_name first, so set it for consistency.
  const name = firstNonEmpty(out.drug_name, out.name, out.drugName, out.medicine_name);
  if (name !== "") out.drug_name = String(name).trim();

  const frequency = normalizeFrequency(firstNonEmpty(out.frequency, out.frequency_code));
  if (frequency) out.frequency = frequency;

  // Only fill duration when it is not already canonical — a doctor-edited value
  // that survived a round trip must win over the raw voice field.
  const existing = parseDuration(out.duration, out.duration_unit);
  const fromVoice = parseDuration(
    firstNonEmpty(out.period, out.duration),
    firstNonEmpty(out.periodUnit, out.period_unit, out.duration_unit)
  );

  const duration = existing.duration !== null ? existing.duration : fromVoice.duration;
  const durationUnit = existing.duration_unit || fromVoice.duration_unit;

  if (duration !== null) out.duration = duration;
  if (durationUnit) out.duration_unit = durationUnit;

  return out;
}

/**
 * Apply normalisation everywhere a medication object can sit inside one
 * care-plan `data` payload.
 *
 * Mirrors medication_payload_for_entry() in main/ipd/manage_care_plan.py: a
 * Medication(3) entry keeps it at data.medication, a template-9 "Orders" entry
 * nests it at data.medication_order.medication. The root itself is covered too
 * because mergeMedicationActivityPayloadForApi() lifts root-level fields into
 * data.medication at persist time.
 */
export function normalizeVoiceCarePlanData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;

  const out = normalizeVoiceMedicationFields(data);

  if (out.medication && typeof out.medication === "object" && !Array.isArray(out.medication)) {
    out.medication = normalizeVoiceMedicationFields(out.medication);
  }

  const orderGroup = out.medication_order;
  if (Array.isArray(orderGroup)) {
    out.medication_order = orderGroup.map((row) =>
      row && typeof row === "object" && row.medication
        ? { ...row, medication: normalizeVoiceMedicationFields(row.medication) }
        : normalizeVoiceMedicationFields(row)
    );
  } else if (orderGroup && typeof orderGroup === "object") {
    out.medication_order = orderGroup.medication
      ? { ...orderGroup, medication: normalizeVoiceMedicationFields(orderGroup.medication) }
      : normalizeVoiceMedicationFields(orderGroup);
  }

  return out;
}

// Does this object look like somebody tried to order a medicine here?
//
// buildInitialValues() seeds every field the structure declares, so `medication`
// is frequently present as an all-blank object even when the dictated fields
// actually landed at the root. Treating that empty shell as a medication would
// raise a "drug name is missing" the doctor cannot clear, and block the save on
// a perfectly good order. A name is deliberately NOT required to qualify — a row
// with a frequency but no drug is exactly the case worth catching.
const hasMedicationSignal = (med) =>
  firstNonEmpty(
    med?.drug_name,
    med?.name,
    med?.drugName,
    med?.medicine_name,
    med?.frequency,
    med?.frequency_code,
    med?.duration,
    med?.period
  ) !== "";

/** Every medication object inside one care-plan `data`, in review order. */
function collectMedications(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  const found = [];

  const push = (med) => {
    if (med && typeof med === "object" && !Array.isArray(med) && hasMedicationSignal(med)) {
      found.push(med);
    }
  };

  push(data.medication);

  const orderGroup = data.medication_order;
  if (Array.isArray(orderGroup)) {
    orderGroup.forEach((row) => push(row?.medication || row));
  } else if (orderGroup && typeof orderGroup === "object") {
    push(orderGroup.medication || orderGroup);
  }

  // A Medication entry whose fields sit at the root (no nesting at all).
  if (found.length === 0) push(data);

  return found;
}

const medicationLabel = (med, index) =>
  String(
    firstNonEmpty(med?.drug_name, med?.name, med?.drugName) || `Medicine ${index + 1}`
  ).trim();

/**
 * Blocking problems for one care-plan `data`, as doctor-readable strings.
 *
 * These are the three things create_medication_order() either rejects outright
 * or silently mangles, checked here so the review screen can stop the save
 * instead of the ward finding out:
 *
 *   - no drug name        -> MedicationOrderError, entry rolled back entirely
 *   - unmapped frequency  -> MedicationOrderError, entry rolled back entirely
 *   - missing duration    -> NOT an error there; quietly becomes 1 day
 *
 * STAT is exempt from the duration check: it is a single immediate dose and
 * generate_medication_schedule() returns before duration is ever used. SOS/PRN
 * still need one — they schedule no tasks, but the duration is what the
 * dispensing quantity is derived from.
 */
export function collectMedicationProblems(data) {
  const problems = [];

  collectMedications(data).forEach((med, index) => {
    const label = medicationLabel(med, index);
    const name = firstNonEmpty(med.drug_name, med.name, med.drugName);
    if (String(name).trim() === "") {
      problems.push(`Medicine ${index + 1}: drug name is missing.`);
      return;
    }

    const rawFrequency = String(firstNonEmpty(med.frequency, med.frequency_code)).trim();
    const frequency = normalizeFrequency(rawFrequency);
    if (!frequency) {
      problems.push(
        rawFrequency
          ? `${label}: frequency "${rawFrequency}" is not a valid order frequency — pick one from the list.`
          : `${label}: frequency is missing — pick one from the list.`
      );
    }

    if (frequency !== "STAT") {
      const { duration, duration_unit: unit } = parseDuration(
        firstNonEmpty(med.duration, med.period),
        firstNonEmpty(med.duration_unit, med.periodUnit, med.period_unit)
      );
      const durationMissingFlag = med.duration_missing === true;
      if (duration === null || duration <= 0 || durationMissingFlag) {
        problems.push(`${label}: duration is missing — the ward would receive a single day.`);
      } else if (!unit) {
        problems.push(`${label}: duration unit is missing — pick Day(s), Week(s) or Month(s).`);
      }
    }
  });

  return problems;
}

// The column an investigation row keeps its name in. Same list
// buildLabTestsFromEntries falls back through, so the two agree on what counts
// as a named row.
export const TEST_NAME_FIELDS = [
  "test_name",
  "name",
  "investigation",
  "test",
  "order_detail",
  "orderDetail",
];

/** The column this row keeps its name in, or "" when it has no name at all. */
export const testRowNameField = (row) => {
  if (!row || typeof row !== "object") return "";
  return (
    TEST_NAME_FIELDS.find((key) => row[key] != null && String(row[key]).trim() !== "") || ""
  );
};

const testRowName = (row) => {
  const field = testRowNameField(row);
  return field ? String(row[field]).trim() : "";
};

// Has this row been matched to a catalogue service?
// Prefer flat medical_service_id from the EMR enricher; fall back to
// __service_meta written by the review picker (or FE mapping of flat fields).
export const testRowHasService = (row) => {
  const flatId = String(
    row?.medical_service_id ?? row?.medicalServiceId ?? ""
  ).trim();
  if (flatId) return true;
  const meta = row?.__service_meta;
  if (!meta || typeof meta !== "object") return false;
  return Object.values(meta).some(
    (entry) => String(entry?.medical_service_id || "").trim() !== ""
  );
};

const testRowMatchStatus = (row) =>
  String(row?.match_status || "").trim().toLowerCase();

/** Every investigation row inside one care-plan `data`, wherever it is nested. */
function collectTestRows(data) {
  const found = [];
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (Array.isArray(node.test_items)) found.push(...node.test_items);
    Object.entries(node).forEach(([key, child]) => {
      if (key === "test_items" || key === "__service_meta") return;
      walk(child);
    });
  };
  walk(data);
  return found.filter((row) => row && typeof row === "object");
}

/**
 * Blocking problems for the investigations in one care-plan `data`.
 *
 * An unmatched row is not a cosmetic gap. add_patient_lab_tests/ stores
 * test[].medical_service_id, and a pre_save signal on PatientLabTest
 * (main/patient/manage_lab_test.py: assign_lab_order_no) only assigns
 * order_no / ordered_time when order_routing_for(medical_service_id) resolves a
 * department. With the id empty it returns immediately, the row is written with
 * no order number, and — in that module's own words — it "appears in no
 * worklist at all, so it is silently lost". The endpoint still answers
 * {"status": "Success"}, so nothing on screen says the lab never got it.
 */
export function collectTestProblems(data) {
  const problems = [];

  collectTestRows(data).forEach((row) => {
    const name = testRowName(row);
    if (!name) return;
    if (testRowHasService(row)) return;

    const status = testRowMatchStatus(row);
    if (status === "ambiguous") {
      const n = Array.isArray(row.match_candidates) ? row.match_candidates.length : 0;
      problems.push(
        n > 0
          ? `${name}: multiple catalogue matches — pick the correct service from the list.`
          : `${name}: ambiguous catalogue match — pick the correct service from the list.`
      );
      return;
    }
    problems.push(
      `${name}: not matched to a catalogue service — this order would never reach the lab. Pick it from the list.`
    );
  });

  return problems;
}

/** Doctor-facing lines from add_ipd_voice_careplan data.warnings[]. */
export function formatVoiceCarePlanWarnings(warnings) {
  if (!Array.isArray(warnings)) return [];
  return warnings
    .map((w) => {
      if (typeof w === "string") return w.trim();
      if (!w || typeof w !== "object") return "";
      const message = String(w.message || "").trim();
      if (message) return message;
      const code = String(w.code || "").trim();
      const name = String(
        w.test_name || w.drug_name || w.raw || w.raw_drug_name || ""
      ).trim();
      if (code && name) return `${code}: ${name}`;
      return code || name;
    })
    .filter(Boolean);
}

/**
 * Review-wide problems, labelled by section so the doctor can find the row.
 * Medication and investigation are checked — the two that are silently dropped
 * downstream rather than rejected loudly.
 */
export function collectVoiceCarePlanProblems(plans) {
  const list = Array.isArray(plans) ? plans : [];
  const problems = [];

  list.forEach((plan) => {
    const type = String(plan?.type || "").trim().toLowerCase();
    if (type === "medication") {
      collectMedicationProblems(plan?.data || {}).forEach((problem) => problems.push(problem));
    } else if (type === "test") {
      collectTestProblems(plan?.data || {}).forEach((problem) => problems.push(problem));
    }
  });

  return problems;
}
